import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useCookieConsent, CookieCategory } from '../../hooks/useCookieConsent';
import { route } from '../../globals/constants';
import { navUrl } from '../../globals/common-functions';
import './CookieConsentBanner.scss';

interface CookieConsentBannerProps {
    position?: 'bottom' | 'top';
    theme?: 'dark' | 'light';
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ 
    position = 'bottom', 
    theme = 'dark' 
}) => {
    const { 
        showBanner, 
        consent, 
        acceptAll, 
        acceptEssential, 
        updateCategory, 
        hideBanner 
    } = useCookieConsent();

    const [showDetails, setShowDetails] = useState(false);

    if (!showBanner) return null;

    const bannerClasses = `cookie-consent-banner ${position} ${theme}`;

    return (
        <div className={bannerClasses}>
            <div className="cookie-banner-overlay" onClick={hideBanner}></div>
            
            <div className="cookie-banner-content" onClick={(e) => e.stopPropagation()}>
                <div className="container">
                    {!showDetails ? (
                        // Simple Banner
                        <div className="cookie-banner-simple">
                            <div className="row align-items-center">
                                <div className="col-lg-8 col-md-7">
                                    <div className="cookie-message">
                                        <h5 className="mb-2">
                                            <i className="feather feather-settings me-2"></i>
                                            Cookie Preferences
                                        </h5>
                                        <p className="mb-0">
                                            We use cookies to enhance your browsing experience, serve personalized content, 
                                            and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                                            <NavLink 
                                                to={navUrl.pages(route.pages.COOKIE_POLICY)} 
                                                className="cookie-policy-link ms-2"
                                            >
                                                Learn more
                                            </NavLink>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-5">
                                    <div className="cookie-actions text-md-end">
                                        <button 
                                            className="btn btn-outline-light btn-sm me-2 mb-2"
                                            onClick={() => setShowDetails(true)}
                                        >
                                            Customize
                                        </button>
                                        <button 
                                            className="btn btn-outline-light btn-sm me-2 mb-2"
                                            onClick={acceptEssential}
                                        >
                                            Essential Only
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm mb-2"
                                            onClick={acceptAll}
                                        >
                                            Accept All
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Detailed Settings
                        <div className="cookie-banner-detailed">
                            <div className="cookie-banner-header d-flex justify-content-between align-items-center mb-4">
                                <h4 className="mb-0">
                                    <i className="feather feather-settings me-2"></i>
                                    Cookie Settings
                                </h4>
                                <button 
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDetails(false)}
                                    aria-label="Close"
                                ></button>
                            </div>

                            <div className="cookie-description mb-4">
                                <p>
                                    We use different types of cookies to optimize your experience on our website. 
                                    You can choose which categories to allow. Please note that disabling some 
                                    cookies may affect your browsing experience.
                                </p>
                            </div>

                            <div className="cookie-categories">
                                {/* Essential Cookies */}
                                <div className="cookie-category">
                                    <div className="category-header d-flex justify-content-between align-items-center">
                                        <div className="category-info">
                                            <h6 className="mb-1">
                                                <i className="feather feather-shield me-2"></i>
                                                Essential Cookies
                                            </h6>
                                            <small className="text-muted">Always active</small>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={true}
                                                disabled={true}
                                                id="essential-switch"
                                            />
                                        </div>
                                    </div>
                                    <p className="category-description small">
                                        These cookies are necessary for the website to function and cannot be switched off. 
                                        They enable basic functions like page navigation and access to secure areas.
                                    </p>
                                </div>

                                {/* Functional Cookies */}
                                <div className="cookie-category">
                                    <div className="category-header d-flex justify-content-between align-items-center">
                                        <div className="category-info">
                                            <h6 className="mb-1">
                                                <i className="feather feather-toggle-right me-2"></i>
                                                Functional Cookies
                                            </h6>
                                            <small className="text-muted">Enhance functionality</small>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={consent.functional}
                                                onChange={(e) => updateCategory(CookieCategory.FUNCTIONAL, e.target.checked)}
                                                id="functional-switch"
                                            />
                                        </div>
                                    </div>
                                    <p className="category-description small">
                                        These cookies enable enhanced functionality and personalization, such as 
                                        remembering your preferences and providing customized features.
                                    </p>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="cookie-category">
                                    <div className="category-header d-flex justify-content-between align-items-center">
                                        <div className="category-info">
                                            <h6 className="mb-1">
                                                <i className="feather feather-bar-chart-2 me-2"></i>
                                                Analytics Cookies
                                            </h6>
                                            <small className="text-muted">Performance insights</small>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={consent.analytics}
                                                onChange={(e) => updateCategory(CookieCategory.ANALYTICS, e.target.checked)}
                                                id="analytics-switch"
                                            />
                                        </div>
                                    </div>
                                    <p className="category-description small">
                                        These cookies help us understand how visitors interact with our website by 
                                        collecting and reporting information anonymously.
                                    </p>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="cookie-category">
                                    <div className="category-header d-flex justify-content-between align-items-center">
                                        <div className="category-info">
                                            <h6 className="mb-1">
                                                <i className="feather feather-target me-2"></i>
                                                Marketing Cookies
                                            </h6>
                                            <small className="text-muted">Personalized advertising</small>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={consent.marketing}
                                                onChange={(e) => updateCategory(CookieCategory.MARKETING, e.target.checked)}
                                                id="marketing-switch"
                                            />
                                        </div>
                                    </div>
                                    <p className="category-description small">
                                        These cookies are used to deliver advertisements more relevant to you and your interests. 
                                        They also limit the number of times you see an advertisement.
                                    </p>
                                </div>
                            </div>

                            <div className="cookie-banner-footer mt-4">
                                <div className="d-flex justify-content-between flex-wrap">
                                    <div className="cookie-links mb-2">
                                        <NavLink 
                                            to={navUrl.pages(route.pages.COOKIE_POLICY)} 
                                            className="btn btn-link btn-sm p-0 me-3"
                                        >
                                            Cookie Policy
                                        </NavLink>
                                        <NavLink 
                                            to={navUrl.pages(route.pages.PRIVACY_POLICY)} 
                                            className="btn btn-link btn-sm p-0"
                                        >
                                            Privacy Policy
                                        </NavLink>
                                    </div>
                                    <div className="cookie-final-actions">
                                        <button 
                                            className="btn btn-outline-light btn-sm me-2"
                                            onClick={acceptEssential}
                                        >
                                            Essential Only
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={acceptAll}
                                        >
                                            Accept All
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CookieConsentBanner; 